/**
 * UI Copy Constants
 *
 * Centralized customer-facing copy that can be easily updated and localized.
 * All text visible to customers should come from here.
 *
 * Language Guidelines (from GLOBAL_COMMERCE_SPECIFICATION):
 * - "The customer is shopping, not manufacturing"
 * - Never say: 3D printed, fulfillment, processing, supplier, SKU, variant
 * - Always say: Handcrafted, delivery, being created, artisan, product, size
 *
 * @module shared/utils/copy
 */

/**
 * Customer-facing copy constants
 */
export const COPY = {
  // ============================================
  // ORDER STATUS
  // Customer sees these as their order progresses
  // ============================================
  orderStatus: {
    confirmed: {
      title: 'Order confirmed',
      message: 'Thank you! Your order is being reviewed.',
      emoji: '✨',
    },
    beingCreated: {
      title: 'Being created',
      message: 'Your creation is being carefully crafted by our artisans.',
      emoji: '🎨',
    },
    shipped: {
      title: 'On its way',
      message: 'Your creation has shipped! Track it below.',
      emoji: '📦',
    },
    delivered: {
      title: 'Delivered',
      message: 'Enjoy your creation!',
      emoji: '🎉',
    },
    cancelled: {
      title: 'Cancelled',
      message: 'This order has been cancelled. Contact us if you need help.',
      emoji: '❌',
    },
  },

  // ============================================
  // CART
  // Shopping bag experience
  // ============================================
  cart: {
    empty: {
      title: 'Your bag is empty',
      message: 'Start creating something special!',
      action: 'Create Now',
      emoji: '🛍️',
    },
    itemAdded: 'Added to your bag',
    itemRemoved: 'Removed from your bag',
    updated: 'Your bag has been updated',
    bagTitle: 'Your Bag',
    continueShopping: 'Continue Shopping',
    checkout: 'Checkout',
    subtotal: 'Subtotal',
  },

  // ============================================
  // CHECKOUT
  // Payment flow
  // ============================================
  checkout: {
    title: 'Complete your order',
    shipping: 'Where should we send it?',
    shippingSubtitle: 'Enter your delivery address',
    payment: 'How would you like to pay?',
    paymentSubtitle: 'All payments are secure and encrypted',
    review: 'Review your order',
    processing: 'Completing your order...',
    success: 'Thank you for your order!',
    successMessage: "We're excited to bring your creation to life.",
    securePayment: 'Secure payment',
    orderSummary: 'Order summary',
    placeOrder: 'Place Order',
    goBack: 'Go Back',
  },

  // ============================================
  // PRODUCTS
  // Product categories and descriptions
  // ============================================
  products: {
    figurines: {
      title: 'Collectible Figurines',
      subtitle: 'Handcrafted just for you',
      description: 'Each figurine is carefully crafted to capture every detail.',
    },
    printProducts: {
      title: 'Print Products',
      subtitle: 'Your design on premium products',
      description: 'Your creation, your way. Premium products for daily life.',
    },
    customization: {
      title: 'Customize Your Creation',
      subtitle: 'Make it uniquely yours',
    },
  },

  // ============================================
  // PRODUCT SIZES
  // Customer-friendly size names (not measurements)
  // ============================================
  sizes: {
    pocket: {
      name: 'Pocket',
      description: 'Tiny treasure to carry everywhere',
    },
    deskBuddy: {
      name: 'Desk Buddy',
      description: 'Perfect companion for your workspace',
    },
    showpiece: {
      name: 'Showpiece',
      description: 'Statement piece for proud display',
    },
  },

  // ============================================
  // ERRORS
  // Friendly error messages (see error-messages.ts for full mapping)
  // ============================================
  errors: {
    generic: 'Something went wrong. Please try again.',
    tryAgain: 'Try Again',
    contactUs: 'Contact Us',
    goHome: 'Go Home',
    refresh: 'Refresh Page',
    networkError: 'Please check your internet connection.',
    sessionExpired: 'Your session has expired. Please sign in again.',
  },

  // ============================================
  // DELIVERY
  // Delivery time messaging
  // ============================================
  delivery: {
    figurines: 'Handcrafted in 10-14 days',
    print: 'Created and shipped in 5-7 days',
    shipping: 'Then 3-5 days for delivery',
    freeShipping: 'Free shipping',
    estimatedDelivery: 'Estimated delivery',
    trackOrder: 'Track your order',
  },

  // ============================================
  // COUNTRY SELECTOR
  // Location selection experience
  // ============================================
  country: {
    welcome: 'Welcome!',
    whereAreYou: 'Where are you shopping from?',
    helpText: 'This helps us show prices in your currency and calculate delivery.',
    changeLocation: 'Change Location',
    continueShopping: 'Continue Shopping',
    searchPlaceholder: 'Search countries...',
    popular: 'Popular',
    allCountries: 'All Countries',
    noResults: 'No countries found',
    shoppingFrom: 'Shopping from',
    moreCountries: 'More countries...',
  },

  // ============================================
  // GENERAL UI
  // Common UI elements
  // ============================================
  ui: {
    loading: 'Loading...',
    saving: 'Saving...',
    saved: 'Saved!',
    cancel: 'Cancel',
    confirm: 'Confirm',
    close: 'Close',
    next: 'Next',
    back: 'Back',
    done: 'Done',
    edit: 'Edit',
    delete: 'Delete',
    remove: 'Remove',
    add: 'Add',
    search: 'Search',
    filter: 'Filter',
    sort: 'Sort',
    viewAll: 'View All',
    seeMore: 'See More',
    learnMore: 'Learn More',
  },

  // ============================================
  // AUTH
  // Authentication flows
  // ============================================
  auth: {
    signIn: 'Sign In',
    signUp: 'Create Account',
    signOut: 'Sign Out',
    welcome: 'Welcome back!',
    welcomeNew: 'Create your account',
    forgotPassword: 'Forgot password?',
    resetPassword: 'Reset Password',
    continueWith: 'Continue with',
    orContinueWith: 'or continue with',
    agreeToTerms: 'By continuing, you agree to our Terms and Privacy Policy.',
  },

  // ============================================
  // HELP & SUPPORT
  // Support and FAQ
  // ============================================
  help: {
    title: 'Help & Support',
    faq: 'Frequently Asked Questions',
    contactUs: 'Contact Us',
    emailSupport: 'Email Support',
    chatSupport: 'Chat with us',
    helpCenter: 'Help Center',
    needHelp: 'Need help?',
    weAreHere: "We're here to help",
    responseTime: 'We typically respond within 24 hours',
  },

  // ============================================
  // EMAIL
  // Email template copy (used in backend)
  // ============================================
  email: {
    orderConfirmation: {
      subject: 'Your Tangi creation is on its way! ✨',
      greeting: 'Thank you for your order!',
      whatsNext: 'What happens next',
      craftingMessage: 'Your unique creation is being carefully crafted by our artisans.',
      trackOrder: 'Track Your Order',
    },
    orderShipped: {
      subject: 'Your creation is on its way! 📦',
      greeting: 'Great news!',
      message: 'Your creation has shipped and is on its way to you.',
      trackPackage: 'Track Package',
    },
    orderDelivered: {
      subject: 'Enjoy your creation! 🎉',
      greeting: 'Your order has been delivered',
      message: 'We hope you love it!',
      share: 'Share your creation',
    },
  },

  // ============================================
  // BRAND
  // Brand messaging
  // ============================================
  brand: {
    tagline: 'Bring your imagination to life',
    mission: 'Making custom creations accessible to everyone',
    quality: 'Premium quality, crafted with care',
    unique: 'One of a kind, just for you',
  },
} as const;

/**
 * Helper to get nested copy by dot path
 * e.g. getCopy('cart.empty.title') -> 'Your bag is empty'
 */
export function getCopy(path: string): string {
  if (!path) return '';

  const keys = path.split('.');
  let current: unknown = COPY;

  for (const key of keys) {
    if (current && typeof current === 'object' && key in (current as Record<string, unknown>)) {
      current = (current as Record<string, unknown>)[key];
    } else {
      return path; // Return key as fallback if not found
    }
  }

  if (typeof current === 'string') {
    return current;
  }

  return path;
}
